// Shared date helpers so tee times round-trip without timezone drift.
//
// Games store a naive "wall clock" time (the time the user picked). We must
// therefore both WRITE and READ it as local wall-clock, never letting the
// browser apply a UTC offset:
//  - toLocalInput: format a Date for a <input type="datetime-local"> in local
//    time (toISOString() would shift it to UTC).
//  - parseLocalDate: parse an API datetime string as local wall-clock, ignoring
//    any trailing Z/offset (so 10:00 stored shows as 10:00, not 11:00/09:00).

export const toLocalInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const parseLocalDate = (value: string | null | undefined): Date => {
  if (!value) return new Date(NaN);
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (!m) return new Date(value);
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] || 0)
  );
};

// --- South African Standard Time (SAST, UTC+2, no DST) ------------------------
// FairPlay is a South Africa-only app: tee times are always SAST. Stored values
// are naive wall-clock times the user picked (meant as SAST). To display them
// correctly on ANY device regardless of the browser's own timezone, we treat the
// stored wall-clock as SAST and render it in Africa/Johannesburg.
const SAST_OFFSET_HOURS = 2;
const SAST_TZ = 'Africa/Johannesburg';

// Interpret a stored naive datetime as SAST and return the matching absolute instant.
export const parseSAST = (value: string | null | undefined): Date => {
  if (!value) return new Date(NaN);
  // Offset-bearing values already describe an instant. Do not discard their zone.
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return new Date(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (!m) return new Date(value);
  return new Date(
    Date.UTC(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]) - SAST_OFFSET_HOURS,
      Number(m[5]),
      Number(m[6] || 0)
    )
  );
};

// Format a stored tee time in SAST, whatever the viewer's local timezone.
export const formatSAST = (
  value: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
): string => {
  const d = parseSAST(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-ZA', { ...opts, timeZone: SAST_TZ });
};

// The current wall-clock in SAST, formatted for a <input type="datetime-local">.
// Use this for defaults so a device in another timezone still prefills SA time.
export const toSASTInput = (base: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SAST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(base);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
};

/** A calendar shortcut in South Africa, independent of the device timezone. */
export const sastTeeTime = (day: 'now' | 'today' | 'tomorrow' | 'weekend', base = new Date()): string => {
  const wallClock = toSASTInput(base);
  if (day === 'now') return wallClock;
  const date = new Date(`${wallClock.slice(0, 10)}T00:00:00Z`);
  const offset = day === 'tomorrow' ? 1 : day === 'weekend' ? (6 - date.getUTCDay() + 7) % 7 : 0;
  date.setUTCDate(date.getUTCDate() + offset);
  const candidate = `${date.toISOString().slice(0, 10)}T10:00`;
  // On the current day, never suggest a time that has already passed.
  return parseSAST(candidate).getTime() < base.getTime() ? wallClock : candidate;
};
